import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  ButtonBase,
  Chip,
  Button,
  Collapse,
} from '@mui/material';
import {
  ArrowForward as ArrowForwardIcon,
  Warning as WarningIcon,
  FlightLand as FlightLandIcon,
  CalendarMonth as CalendarIcon,
  HomeWork as HomeWorkIcon,
  EmojiNature as MapleIcon,
  TrendingUp as TrendingUpIcon,
  ReceiptLong as ReceiptIcon,
  House as HouseIcon,
  ChildCare as ChildCareIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import SEOHead from '../components/SEO/SEOHead';
import PageHeader from '../components/Layout/PageHeader';
import TrustStrip from '../components/Layout/TrustStrip';
import ReferralCodeBlock from '../components/shared/ReferralCodeBlock';
import { useLocalizedPath } from '../hooks/useLocalizedPath';
import { gtmQuizCompleted, gtmNewsletterSubmit } from '../utils/gtm';

/* ─── Analytics helper ────────────────────────────────────────────── */
const trackEvent = (name, props) => {
  if (window.plausible) window.plausible(name, { props });
  if (window.dataLayer) window.dataLayer.push({ event: name.toLowerCase().replace(/\s+/g, '_'), ...props });
};

/* ─── Quiz options ────────────────────────────────────────────────── */
const QUIZ_OPTIONS = [
  { id: 'just_arrived', label: 'Acabo de llegar (0-3 meses)', sub: 'Cuenta, nómina y primeros pasos', Icon: FlightLandIcon },
  { id: 'under_1yr', label: 'Menos de 1 año', sub: 'Impuestos, crédito y ahorro', Icon: CalendarIcon },
  { id: '1_to_3yr', label: 'Entre 1 y 3 años', sub: 'Hipoteca, FHSA y patrimonio', Icon: HomeWorkIcon },
  { id: 'over_3yr', label: 'Más de 3 años', sub: 'Inversión, RESP y optimización', Icon: MapleIcon },
];

/* ─── Segment data ────────────────────────────────────────────────── */
const SEGMENTS = {
  just_arrived: {
    intro: 'No necesitas hacer todo hoy. Este es el orden que más sentido tiene para tu etapa.',
    steps: ['Cuenta bancaria sin fees', 'Construir crédito', 'Calcular salario neto'],
    primaryCard: {
      title: 'Abre una cuenta de banco que no te cobre de más',
      text: 'Si acabas de llegar, esta suele ser la primera decisión financiera importante. Te ayuda a recibir nómina, pagar bills y empezar con el pie derecho.',
      cta: 'Ver mejores cuentas para newcomers',
      routeKey: 'accountComparator',
      referralBlock: {
        title: 'Clé Orangeᴹᴰ — Bono de Tangerine',
        description: 'Si eliges Tangerine, usa este código al abrir tu cuenta para aplicar al bono de bienvenida si cumples las condiciones.',
        code: '60870716S1',
        copyButtonLabel: 'Copiar código',
        copiedLabel: 'Copiado',
        disclosure: 'Aplican condiciones. FinMaple puede recibir una compensación si usas este código.',
        partner: 'tangerine',
      },
    },
    secondaryCards: [
      {
        title: 'Construye crédito sin cometer errores tontos',
        text: 'Tu historial en otro país normalmente no se traslada. Aquí tu credit score empieza de cero — es un número que los bancos revisan para TODO: alquilar, comprar auto, sacar hipoteca. La forma más rápida de empezar: una tarjeta para newcomers.',
        cta: 'Ver tarjetas para newcomers',
        routeKey: 'creditCards',
      },
      {
        title: 'Calcula cuánto te queda de salario después de impuestos',
        text: 'Tu empleador dice "$55,000". Pero después de impuestos federales, provinciales, QPP y EI, te quedan ~$40,000. Eso es ~$3,333/mes en tu cuenta. Saberlo es el primer paso para presupuestar.',
        cta: 'Calcular mi salario neto',
        routeKey: 'salaryCalc',
      },
    ],
    collapsedCards: [
      {
        title: 'Entiende tus impuestos y cobra lo que te deben',
        text: 'El gobierno te DEVUELVE dinero si declaras. Hasta $2,000+/año en créditos gratis (GST/HST, Solidarity Tax Credit). No declarar tu primer año = perder dinero.',
        cta: 'Ver guía de impuestos',
        routeKey: 'blogGuiaQuebec',
      },
      {
        title: 'Empieza a ahorrar con la cuenta correcta',
        text: 'Canadá tiene "alcancías mágicas" del gobierno: TFSA (crece sin impuestos), RRSP (reduce impuestos hoy), FHSA (para tu primera casa). Cada una sirve para algo diferente.',
        cta: '¿Cuál me conviene? Calcular',
        routeKey: 'rrspCalc',
      },
      {
        title: 'Revisa tu credit score (gratis)',
        text: 'Después de 3-6 meses con tarjeta, ya tienes un número. Revisarlo es gratis y no afecta tu score.',
        cta: 'Revisar score gratis',
        link: 'https://borrowell.com/refer-a-friend/free-credit-score?utm_campaign=Refer5&utm_medium=web&utm_source=refer2022-3818681',
        external: true,
      },
    ],
    errors: [
      'Abrir la primera cuenta sin comparar fees — terminas pagando $17/mes por algo que puede ser gratis',
      'Pedir una tarjeta sin entender cómo funciona el crédito — un pago tarde te persigue 6 años',
      'No declarar impuestos el primer año — pierdes $1,000-2,000+ en créditos gratis',
    ],
    emailCopy: {
      title: 'Tu checklist de 90 días',
      text: 'Te enviamos tu checklist de los primeros 90 días y los recursos clave en español.',
      cta: 'Recibir checklist',
    },
  },

  under_1yr: {
    intro: 'Ya tienes las bases. Ahora toca optimizar para quedarte con más dinero.',
    steps: ['Declarar impuestos', 'Optimizar ahorro', 'Mejorar tarjeta'],
    primaryCard: {
      title: 'Declara impuestos y cobra tus créditos',
      text: 'El gobierno te devuelve dinero que ya es tuyo. GST/HST Credit, Solidarity Tax Credit (Quebec), Canada Child Benefit. No declarar = perder dinero gratis.',
      cta: 'Ver guía de impuestos',
      routeKey: 'blogGuiaQuebec',
    },
    secondaryCards: [
      {
        title: 'Optimiza tu ahorro: TFSA vs RRSP',
        text: 'Ahora que tienes ingreso canadiense, puedes elegir dónde poner tu ahorro para pagar menos impuestos o crecer libre de impuestos.',
        cta: 'Calculadora RRSP vs TFSA',
        routeKey: 'rrspCalc',
      },
      {
        title: 'Mejora tu tarjeta de crédito',
        text: 'Si llevas 6+ meses con tu tarjeta y pagas a tiempo, probablemente ya calificas para una tarjeta con cashback o mejores beneficios.',
        cta: 'Comparar tarjetas',
        routeKey: 'creditCards',
      },
    ],
    collapsedCards: [
      {
        title: 'Presupuesta tu vida en Canadá',
        text: 'Saber exactamente a dónde va tu dinero es la base de toda decisión financiera inteligente.',
        cta: 'Crear mi presupuesto',
        routeKey: 'budgetPlanner',
      },
      {
        title: 'Envía dinero a tu familia',
        text: 'Compara opciones para enviar remesas y ahorra en comisiones y tipo de cambio.',
        cta: 'Comparar remesas',
        routeKey: 'remittanceComparator',
      },
      {
        title: 'Revisa tu credit score (gratis)',
        text: 'Si llevas 6+ meses con tarjeta y pagas a tiempo, ya deberías tener un score. Revisarlo no lo afecta.',
        cta: 'Revisar score gratis',
        link: 'https://borrowell.com/refer-a-friend/free-credit-score?utm_campaign=Refer5&utm_medium=web&utm_source=refer2022-3818681',
        external: true,
      },
    ],
    errors: [
      'No declarar impuestos porque "gano poco" — pierdes créditos gratis del gobierno',
      'Dejar tu ahorro en la cuenta corriente sin TFSA — estás perdiendo crecimiento libre de impuestos',
      'Pagar comisiones altas en remesas sin comparar opciones',
    ],
    emailCopy: {
      title: '¿Quieres esta ruta por email?',
      text: 'Te enviamos la guía para no perder créditos ni ahorrar mal.',
      cta: 'Recibir guía',
    },
  },

  '1_to_3yr': {
    intro: 'Ya tienes las bases. Ahora toca organizar tu dinero para tu próxima gran meta.',
    steps: ['Optimizar ahorro', 'Preparar compra de casa', 'Organizar presupuesto'],
    primaryCard: {
      title: 'Optimiza tu ahorro para tu próxima gran meta',
      text: 'Ya no estás en modo supervivencia. Ahora te conviene decidir si tu prioridad es comprar casa, invertir o pagar menos impuestos. La cuenta correcta cambia mucho el resultado.',
      cta: 'Calcular RRSP vs TFSA',
      routeKey: 'rrspCalc',
      tip: 'Si planeas comprar tu primera casa en los próximos años, revisa FHSA antes de seguir ahorrando en la cuenta corriente.',
    },
    secondaryCards: [
      {
        title: 'Prepárate para comprar casa sin improvisar',
        text: 'Si tu meta es comprar en 1 a 3 años, empieza por tu ahorro, tu crédito y tu presupuesto antes de mirar propiedades.',
        cta: 'Explorar hipotecas',
        routeKey: 'mortgages',
      },
      {
        title: 'Organiza tu presupuesto para avanzar más rápido hacia tu meta',
        text: 'Cuando tu ingreso ya es más estable, pequeñas fugas mensuales frenan mucho tu avance.',
        cta: 'Crear mi presupuesto',
        routeKey: 'budgetPlanner',
      },
    ],
    collapsedCards: [
      {
        title: 'Revisa tu seguro y protección',
        text: 'A medida que acumulas más patrimonio, conviene revisar qué protección necesitas.',
        cta: 'Comparar seguros',
        routeKey: 'insuranceComparator',
      },
    ],
    errors: [
      'No abrir FHSA si planeas comprar casa — pierdes hasta $8,000 al año de espacio útil',
      'Ahorrar tu próxima gran meta en la cuenta equivocada — pagas más impuestos o rindes menos',
      'Empezar a mirar hipotecas sin revisar tu presupuesto y tu crédito — una sorpresa te puede costar miles',
    ],
    emailCopy: {
      title: '¿Quieres esta ruta por email?',
      text: 'Te enviamos la checklist para ordenar ahorro, FHSA, crédito y próximos pasos en Canadá.',
      cta: 'Recibir checklist',
    },
  },

  over_3yr: {
    intro: 'Ya conoces el sistema. Ahora toca hacer crecer tu patrimonio y optimizar impuestos.',
    steps: ['Inversión y crecimiento', 'RESP / educación', 'Planificación fiscal'],
    primaryCard: {
      title: 'Haz crecer tu patrimonio con la estrategia correcta',
      text: 'Ya tienes estabilidad. Ahora toca decidir cómo invertir: RRSP para pagar menos impuestos, TFSA para crecer libre de impuestos, o una combinación según tu tasa marginal.',
      cta: 'Calculadora RRSP vs TFSA',
      routeKey: 'rrspCalc',
    },
    secondaryCards: [
      {
        title: 'Ahorra para la educación de tus hijos con RESP',
        text: 'El gobierno agrega 20% gratis a lo que ahorres (hasta $500/año por hijo). Es dinero gratis que pierdes si no abres la cuenta.',
        cta: 'Ver guía RESP',
        routeKey: 'financlopedia',
        routeSuffix: '/resp',
      },
      {
        title: 'Optimiza tu planificación fiscal',
        text: 'Cuando tu ingreso crece, tu tasa marginal sube. Estrategias como maximizar RRSP o income splitting pueden ahorrarte miles al año.',
        cta: 'Guía de impuestos',
        routeKey: 'blogGuiaQuebec',
      },
    ],
    collapsedCards: [
      {
        title: 'Renueva hipoteca o compra con mejor tasa',
        text: 'Si tu hipoteca viene a renovación, no aceptes la primera oferta de tu banco. Comparar puede ahorrarte 0.5%+ en tasa.',
        cta: 'Comparar tasas hipotecarias',
        routeKey: 'mortgages',
      },
      {
        title: 'Compara opciones para enviar remesas',
        text: 'Si envías dinero regularmente, comparar servicios puede ahorrarte cientos al año en comisiones y tipo de cambio.',
        cta: 'Comparar remesas',
        routeKey: 'remittanceComparator',
      },
    ],
    errors: [
      'No maximizar RRSP cuando tu tasa marginal ya es alta — pierdes cientos en impuestos cada año',
      'Renovar hipoteca con tu banco actual sin comparar — puedes estar pagando 0.5%+ de más',
      'No tener seguro de vida o incapacidad cuando ya tienes dependientes o hipoteca',
    ],
    emailCopy: {
      title: '¿Quieres esta ruta por email?',
      text: 'Te enviamos la guía para optimizar patrimonio e impuestos en Canadá.',
      cta: 'Recibir guía',
    },
  },
};

/* ─── 90-day roadmap (just_arrived email capture) ────────────────── */
const ROADMAP_90 = [
  { phase: 'Semana 1', color: '#D61F2C', items: ['Abrir cuenta bancaria sin fees', 'Obtener SIN y activar nómina', 'Solicitar tarjeta newcomer'] },
  { phase: 'Mes 1', color: '#F9A825', items: ['Configurar pagos automáticos', 'Entender TFSA vs RRSP vs FHSA', 'Calcular tu salario neto real'] },
  { phase: 'Mes 3', color: '#2E7D32', items: ['Revisar credit score (gratis)', 'Declarar impuestos + cobrar créditos', 'Crear presupuesto mensual'] },
];

/* ─── SEO Schema ──────────────────────────────────────────────────── */
const howToSchema = {
  '@context': 'https://schema.org',
  '@type': 'HowTo',
  name: 'Ruta financiera para latinos en Canadá',
  description: 'Guía interactiva según cuánto tiempo llevas en Canadá. Cuenta bancaria, crédito, impuestos y ahorro.',
  step: [
    { '@type': 'HowToStep', name: 'Responde 1 pregunta', text: 'Indica cuánto tiempo llevas en Canadá' },
    { '@type': 'HowToStep', name: 'Ve tu plan personalizado', text: 'Recibe pasos ordenados por prioridad' },
    { '@type': 'HowToStep', name: 'Toma acción', text: 'Sigue los links a herramientas y comparadores' },
  ],
};

/* Task 1: No breadcrumbs on funnel entry pages */

/* ─── ActionCard component ────────────────────────────────────────── */
const ActionCard = ({ card, isPrimary, isCollapsed, isLater, stepLabel, stepNumber, lng, segment, lp }) => {
  const isStep3 = stepNumber === 3;
  const isExternal = card.external;
  const resolvedPath = isExternal
    ? card.link
    : lp(card.routeKey) + (card.routeSuffix || '');
  const linkProps = isExternal
    ? { component: 'a', href: card.link, target: '_blank', rel: 'noopener noreferrer' }
    : { component: RouterLink, to: resolvedPath };

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: '12px',
        mb: isPrimary ? 2.5 : 2,
        transition: 'all 200ms ease',
        ...(isPrimary
          ? {
              p: { xs: 3, md: '40px' },
              bgcolor: '#FAFBFC',
              border: '1px solid #E5E7EB',
              borderLeft: '4px solid #1B2A4A',
            }
          : isLater
            ? {
                p: { xs: 2, md: '20px' },
                bgcolor: '#FFFFFF',
                border: '1px solid #EEF0F3',
              }
            : isStep3
              ? {
                  p: { xs: 2, md: '22px' },
                  bgcolor: '#FFFFFF',
                  border: '1px solid #EEF0F3',
                }
              : {
                  p: { xs: 2, md: '24px' },
                  bgcolor: '#FFFFFF',
                  border: '1px solid #E5E7EB',
                }),
        '&:hover': { boxShadow: isPrimary ? '0 6px 24px rgba(0,0,0,0.08)' : '0 4px 16px rgba(0,0,0,0.06)' },
      }}
    >
      {/* Step label chip */}
      {stepLabel && (
        <Chip
          label={stepLabel}
          size="small"
          sx={{
            mb: 1.5,
            height: 22,
            fontSize: '11px',
            fontWeight: 700,
            bgcolor: isPrimary ? '#1E3A5F' : 'rgba(30,58,95,0.08)',
            color: isPrimary ? '#FFFFFF' : '#1E3A5F',
          }}
        />
      )}

      <Typography
        variant="h6"
        component="h3"
        sx={{
          fontWeight: 600,
          fontSize: isPrimary ? { xs: '17px', md: '20px' } : isLater ? { xs: '15px', md: '16px' } : isStep3 ? { xs: '15px', md: '17px' } : { xs: '16px', md: '18px' },
          color: '#1A1A1A',
          mb: isPrimary ? 1.5 : 1,
          lineHeight: 1.3,
        }}
      >
        {card.title}
      </Typography>

      <Typography sx={{ fontSize: (isLater || isStep3) ? '13px' : '14px', color: '#5F6B7A', lineHeight: 1.7, mb: isPrimary ? 2.5 : isLater ? 1.5 : isStep3 ? 1.5 : 2 }}>
        {card.text}
      </Typography>

      {isCollapsed || isLater ? (
        <Button
          endIcon={<ArrowForwardIcon sx={{ fontSize: '14px !important' }} />}
          onClick={() => trackEvent('Result Secondary CTA', { segment, destination: resolvedPath, locale: lng })}
          {...linkProps}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '13px',
            px: 0,
            color: '#1E3A5F',
            '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' },
          }}
        >
          {card.cta}
        </Button>
      ) : (
        <Button
          variant={(isPrimary && !card.referralBlock) ? 'contained' : 'outlined'}
          disableElevation
          endIcon={<ArrowForwardIcon />}
          onClick={() => trackEvent(isPrimary ? 'Result Primary CTA' : 'Result Secondary CTA', { segment, destination: resolvedPath, locale: lng })}
          {...linkProps}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '13px',
            px: 2.5,
            py: 1,
            borderRadius: '10px',
            ...((isPrimary && !card.referralBlock)
              ? { bgcolor: '#1E3A5F', '&:hover': { bgcolor: '#1A3152' } }
              : { borderColor: '#E5E7EB', color: '#1E3A5F', '&:hover': { borderColor: '#1E3A5F', bgcolor: 'rgba(30,58,95,0.04)' } }),
          }}
        >
          {card.cta}
        </Button>
      )}

      {isPrimary && card.referralBlock && (
        <ReferralCodeBlock {...card.referralBlock} />
      )}
    </Paper>
  );
};

/* ─── GoalSelector component (3+ years) ──────────────────────────── */
const GoalSelector = ({ goals, lng, lp }) => {
  const [expanded, setExpanded] = useState(null);

  return (
    <Box sx={{ mb: 3 }}>
      <Typography
        variant="h6"
        component="h3"
        sx={{ fontWeight: 600, fontSize: '16px', color: '#1A1A1A', mb: 2, textAlign: 'center' }}
      >
        {goals.question}
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          gap: 2,
        }}
      >
        {goals.options.map((goal, i) => {
          const IconComp = goal.Icon;
          const isOpen = expanded === i;
          return (
            <Paper
              key={i}
              elevation={0}
              sx={{
                border: '1px solid',
                borderColor: isOpen ? '#1E3A5F' : '#EEF0F3',
                borderRadius: '14px',
                overflow: 'hidden',
                transition: 'all 200ms ease',
                '&:hover': {
                  borderColor: '#1E3A5F',
                  boxShadow: '0 4px 16px rgba(0,0,0,0.06)',
                },
              }}
            >
              <ButtonBase
                onClick={() => {
                  setExpanded(isOpen ? null : i);
                  trackEvent('Goal Selected', { goal: goal.label });
                }}
                sx={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  p: 2.5,
                  justifyContent: 'flex-start',
                }}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    minWidth: 40,
                    borderRadius: '10px',
                    bgcolor: isOpen ? 'rgba(30,58,95,0.08)' : 'rgba(30,58,95,0.04)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'background 200ms ease',
                  }}
                >
                  <IconComp sx={{ fontSize: 20, color: '#1E3A5F' }} />
                </Box>
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: '14px',
                    color: '#1A1A1A',
                    textAlign: 'left',
                    flex: 1,
                  }}
                >
                  {goal.label}
                </Typography>
                <ExpandMoreIcon
                  sx={{
                    fontSize: 20,
                    color: '#5F6B7A',
                    transition: 'transform 200ms ease',
                    transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                  }}
                />
              </ButtonBase>

              <Collapse in={isOpen}>
                <Box sx={{ px: 2.5, pb: 2.5, pt: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {goal.links.map((link, li) => (
                    <Button
                      key={li}
                      component={RouterLink}
                      to={link.routeKey ? lp(link.routeKey) + (link.routeSuffix || '') : `/${lng}${link.href}`}
                      variant="outlined"
                      size="small"
                      endIcon={<ArrowForwardIcon sx={{ fontSize: '14px !important' }} />}
                      onClick={() => trackEvent('result_secondary_cta_clicked', { segment: 'over_3yr', card: link.text, locale: lng })}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '13px',
                        px: 2,
                        py: 0.75,
                        borderRadius: '8px',
                        borderColor: '#EEF0F3',
                        color: '#1E3A5F',
                        justifyContent: 'space-between',
                        '&:hover': { borderColor: '#1E3A5F', bgcolor: 'rgba(30,58,95,0.04)' },
                      }}
                    >
                      {link.text}
                    </Button>
                  ))}
                </Box>
              </Collapse>
            </Paper>
          );
        })}
      </Box>
    </Box>
  );
};

/* ─── ErroresComunes component ────────────────────────────────────── */
const ErroresComunes = ({ errors }) => (
  <Box sx={{ mt: 5 }}>
    <Typography
      variant="h6"
      component="h3"
      sx={{ fontWeight: 600, fontSize: '16px', color: '#1A1A1A', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}
    >
      <WarningIcon sx={{ color: '#F59E0B', fontSize: 20 }} />
      Errores comunes en esta etapa
    </Typography>
    <Box
      sx={{
        bgcolor: '#FFFBEB',
        borderRadius: '12px',
        borderLeft: '3px solid #F59E0B',
        p: 2.5,
      }}
    >
      {errors.map((err, i) => (
        <Typography
          key={i}
          sx={{
            fontSize: '14px',
            color: '#5F6B7A',
            lineHeight: 1.7,
            mb: i < errors.length - 1 ? 1.5 : 0,
            pl: 3,
            position: 'relative',
            '&::before': {
              content: `"${i + 1}."`,
              position: 'absolute',
              left: 0,
              fontWeight: 600,
              color: '#92400E',
            },
          }}
        >
          {err}
        </Typography>
      ))}
    </Box>
  </Box>
);

/* ─── RecommendedOrderSummary component ───────────────────────────── */
const RecommendedOrderSummary = ({ steps }) => {
  if (!steps || steps.length === 0) return null;
  return (
    <Box
      sx={{
        mt: 1,
        mb: 3.5,
        p: 2.5,
        bgcolor: 'rgba(30,58,95,0.04)',
        borderRadius: '12px',
        border: '1px solid #E5E7EB',
      }}
    >
      <Typography
        sx={{
          fontSize: '11px',
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
          color: '#1B2A4A',
          mb: 1.5,
        }}
      >
        Tu orden recomendado
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.25, md: 2 }, flexWrap: 'wrap' }}>
        {steps.map((step, i) => (
          <React.Fragment key={i}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  bgcolor: i === 0 ? '#1E3A5F' : 'rgba(30,58,95,0.12)',
                  color: i === 0 ? '#FFFFFF' : '#1E3A5F',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '12px',
                  fontWeight: 700,
                  flexShrink: 0,
                }}
              >
                {i + 1}
              </Box>
              <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#1A1A1A', whiteSpace: 'nowrap' }}>
                {step}
              </Typography>
            </Box>
            {i < steps.length - 1 && (
              <ArrowForwardIcon sx={{ fontSize: 14, color: '#B0B8C4', display: { xs: 'none', sm: 'block' } }} />
            )}
          </React.Fragment>
        ))}
      </Box>
    </Box>
  );
};

/* ─── SectionLabel component ─────────────────────────────────────── */
const SectionLabel = ({ label }) => (
  <Typography
    sx={{
      fontSize: '12px',
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.06em',
      color: '#5F6B7A',
      mt: 3,
      mb: 1.5,
      pl: 0.5,
    }}
  >
    {label}
  </Typography>
);

/* ─── EmailCapture component ──────────────────────────────────────── */
const EmailCapture = ({ copy, segmentId, lng }) => {
  const showRoadmap = segmentId === 'just_arrived';

  return (
    <Paper
      elevation={0}
      sx={{
        mt: 4,
        p: { xs: 2.5, md: 3 },
        borderRadius: '14px',
        bgcolor: '#F7F9FC',
        border: '1px solid #EEF0F3',
      }}
    >
      <Box sx={{ textAlign: 'center', mb: showRoadmap ? 2.5 : 2 }}>
        <Typography sx={{ fontWeight: 600, fontSize: '16px', color: '#1A1A1A', mb: 0.5 }}>
          {copy.title}
        </Typography>
        <Typography sx={{ fontSize: '14px', color: '#5F6B7A' }}>
          {copy.text}
        </Typography>
      </Box>

      {/* 90-day visual roadmap — only for just_arrived */}
      {showRoadmap && (
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, mb: 3 }}>
          {ROADMAP_90.map((phase, pi) => (
            <Box
              key={pi}
              sx={{
                flex: 1,
                borderLeft: { xs: `3px solid ${phase.color}`, md: 'none' },
                borderTop: { xs: 'none', md: `3px solid ${phase.color}` },
                pl: { xs: 2, md: 0 },
                pt: { xs: 0, md: 1.5 },
              }}
            >
              <Typography sx={{ fontWeight: 700, fontSize: '13px', color: phase.color, mb: 0.75, letterSpacing: '0.02em' }}>
                {phase.phase}
              </Typography>
              {phase.items.map((item, ii) => (
                <Box key={ii} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, mb: 0.5 }}>
                  <Box
                    sx={{
                      width: 16, height: 16, minWidth: 16, mt: '2px',
                      borderRadius: '4px', border: '1.5px solid #C5CAD0', bgcolor: '#FFFFFF',
                    }}
                  />
                  <Typography sx={{ fontSize: '13px', color: '#3A4550', lineHeight: 1.4 }}>
                    {item}
                  </Typography>
                </Box>
              ))}
            </Box>
          ))}
        </Box>
      )}

      {/* Email form */}
      <Box sx={{ textAlign: 'center' }}>
        {showRoadmap && (
          <Typography sx={{ fontSize: '13px', color: '#5F6B7A', mb: 1.5 }}>
            Recibe la versión completa con links directos y recordatorios por email.
          </Typography>
        )}
        <Box
          component="form"
          action="https://buttondown.com/api/emails/embed-subscribe/Finmaple"
          method="post"
          target="_blank"
          sx={{
            display: 'flex',
            gap: 1,
            maxWidth: 420,
            mx: 'auto',
            flexDirection: { xs: 'column', sm: 'row' },
          }}
        >
          <input type="hidden" name="tag" value={`empieza-aqui-${segmentId}`} />
          <Box
            component="input"
            name="email"
            type="email"
            placeholder="tu@email.com"
            required
            inputMode="email"
            autoComplete="email"
            sx={{
              flex: 1, px: 2, py: { xs: 2, sm: 1.5 },
              border: '1px solid #EEF0F3', borderRadius: '10px',
              fontSize: '16px', outline: 'none',
              '&:focus': { borderColor: '#1E3A5F' },
            }}
          />
          <Button
            type="submit"
            variant="contained"
            disableElevation
            onClick={() => { trackEvent('newsletter_submit', { segment: segmentId, locale: lng }); gtmNewsletterSubmit({ source: 'empieza-aqui' }); }}
            sx={{
              textTransform: 'none', fontWeight: 600, fontSize: '15px',
              px: 3, py: { xs: 2, sm: 1.5 }, borderRadius: '10px',
              minHeight: 44,
              bgcolor: '#D61F2C', '&:hover': { bgcolor: '#B71C1C' },
              whiteSpace: 'nowrap',
            }}
          >
            {copy.cta}
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};

/* ─── Main Page Component ─────────────────────────────────────────── */
const EmpiezaAquiPage = () => {
  const { t, i18n } = useTranslation();
  const { lng } = useParams();
  const lp = useLocalizedPath();
  const [segment, setSegment] = useState(null);
  const [quizStarted, setQuizStarted] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const resultsRef = useRef(null);

  useEffect(() => {
    trackEvent('empieza_aqui_view', { locale: lng });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSegmentSelect = useCallback((id) => {
    if (!quizStarted) {
      trackEvent('quiz_option_clicked', { segment: id, locale: lng });
      setQuizStarted(true);
    }
    setSegment(id);
    setShowMore(false);
    trackEvent('quiz_completed', { segment: id, locale: lng });
    gtmQuizCompleted({ quiz: 'empieza_aqui', segment: id, locale: lng });
    setTimeout(() => {
      if (resultsRef.current) {
        const y = resultsRef.current.getBoundingClientRect().top + window.scrollY - 80;
        window.scrollTo({ top: y, behavior: 'smooth' });
      }
    }, 100);
  }, [quizStarted, lng]);

  const handleReset = useCallback(() => {
    trackEvent('change_answer_clicked', { locale: lng });
    setSegment(null);
    setQuizStarted(false);
    setShowMore(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [lng]);

  const segmentData = segment ? SEGMENTS[segment] : null;
  const segmentLabel = segment ? QUIZ_OPTIONS.find(o => o.id === segment)?.label : '';

  return (
    <Box sx={{ bgcolor: '#FFFFFF', flex: 1, display: 'flex', flexDirection: 'column' }}>
      <SEOHead
        seoKey="getStarted"
        title="Empieza Aquí - Tu ruta financiera en Canadá | FinMaple"
        description="Responde 1 pregunta y te mostramos qué hacer primero con tu cuenta bancaria, crédito, impuestos y ahorro en Canadá."
        canonical="/empieza-aqui"
        routeKey="getStarted"
        schema={howToSchema}
        noIndex={i18n.language !== 'es'}
      />

      {/* Centered content area — fills available space */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: !segment ? 'center' : 'flex-start' }}>

      {/* Full hero — only when quiz is showing */}
      {!segment && (
        <PageHeader
          variant="minimal"
          title="Tu ruta financiera en Canadá, según cuánto tiempo llevas aquí"
          subtitle="Responde 1 pregunta y te mostramos qué hacer primero con tu cuenta bancaria, crédito, impuestos y ahorro."
          maxWidth="md"
        >
          <Box sx={{ display: 'flex', gap: 0.75, mt: 0.25, flexWrap: 'wrap', justifyContent: 'center' }}>
            <Chip label={t('trustStrip.dataUpdated', 'Actualizado marzo 2026')} size="small" variant="outlined" sx={{ fontSize: '11px', height: 24, borderColor: '#EEF0F3', color: '#5F6B7A' }} />
            <Chip label={t('trustStrip.sources', 'Fuentes oficiales')} size="small" variant="outlined" sx={{ fontSize: '11px', height: 24, borderColor: '#EEF0F3', color: '#5F6B7A' }} />
            <Chip label={t('trustStrip.notAdvice', 'Guía práctica, no asesoría')} size="small" variant="outlined" sx={{ fontSize: '11px', height: 24, borderColor: '#EEF0F3', color: '#5F6B7A' }} />
          </Box>
        </PageHeader>
      )}

      <Container maxWidth="md" sx={{ pb: { xs: 1, md: 1.5 }, mt: !segment ? { xs: 3, md: 4 } : 0 }}>
        {/* ─── Quiz Section ─── */}
        {!segment && (
          <Paper
            elevation={0}
            sx={{
              border: '1px solid #EEF0F3',
              borderRadius: '16px',
              p: { xs: 2, md: 3 },
              textAlign: 'center',
              boxShadow: '0 8px 32px rgba(0,0,0,0.06)',
            }}
          >
            <Typography
              variant="h5"
              component="h2"
              sx={{
                fontWeight: 700,
                fontSize: { xs: '16px', md: '18px' },
                color: '#1A1A1A',
                mb: 0.25,
              }}
            >
              ¿Cuánto tiempo llevas en Canadá?
            </Typography>
            <Typography sx={{ fontSize: '13px', color: '#5F6B7A', mb: { xs: 1.5, md: 2 } }}>
              1 pregunta · menos de 30 segundos
            </Typography>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 2,
                maxWidth: 540,
                mx: 'auto',
              }}
            >
              {QUIZ_OPTIONS.map((opt) => {
                const IconComp = opt.Icon;
                return (
                  <ButtonBase
                    key={opt.id}
                    onClick={() => handleSegmentSelect(opt.id)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      p: 2.5,
                      borderRadius: '14px',
                      border: '2px solid',
                      borderColor: '#EEF0F3',
                      bgcolor: '#FFFFFF',
                      cursor: 'pointer',
                      transition: 'all 180ms ease',
                      '&:hover': {
                        borderColor: '#1E3A5F',
                        bgcolor: 'rgba(30,58,95,0.03)',
                        boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                        transform: 'translateY(-2px)',
                      },
                      '&:active': {
                        transform: 'translateY(0)',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                      },
                      '&:focus-visible': {
                        outline: '2px solid #1E3A5F',
                        outlineOffset: '2px',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 44, height: 44, minWidth: 44,
                        borderRadius: '12px', bgcolor: 'rgba(30,58,95,0.06)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}
                    >
                      <IconComp sx={{ fontSize: 22, color: '#1E3A5F' }} />
                    </Box>
                    <Box sx={{ textAlign: 'left', flex: 1 }}>
                      <Typography sx={{ fontWeight: 600, fontSize: { xs: '14px', md: '15px' }, color: '#1A1A1A', lineHeight: 1.3 }}>
                        {opt.label}
                      </Typography>
                      {opt.sub && (
                        <Typography sx={{ fontSize: '12px', color: '#5F6B7A', mt: 0.25, lineHeight: 1.3 }}>
                          {opt.sub}
                        </Typography>
                      )}
                    </Box>
                  </ButtonBase>
                );
              })}
            </Box>
          </Paper>
        )}

        {/* ─── Results Section ─── */}
        {segment && segmentData && (
          <Box ref={resultsRef}>
            {/* Compact result header */}
            <Box sx={{ mb: 3, pt: { xs: 2, md: 3 } }}>
              <Typography
                variant="h5"
                component="h2"
                sx={{ fontWeight: 700, fontSize: { xs: '22px', md: '26px' }, color: '#1A1A1A', mb: 0.5 }}
              >
                Tu plan recomendado
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Chip
                  label={segmentLabel}
                  size="small"
                  sx={{ bgcolor: 'rgba(30,58,95,0.08)', color: '#1E3A5F', fontWeight: 600, fontSize: '12px' }}
                />
                <Typography sx={{ color: '#D1D5DB', fontSize: '12px', lineHeight: 1 }}>·</Typography>
                <Button
                  variant="text"
                  onClick={handleReset}
                  sx={{ textTransform: 'none', fontSize: '13px', color: '#5F6B7A', p: 0, minWidth: 'auto', '&:hover': { color: '#1E3A5F', bgcolor: 'transparent' } }}
                >
                  Cambiar respuesta
                </Button>
              </Box>
            </Box>

            <Typography sx={{ fontSize: '15px', color: '#5F6B7A', lineHeight: 1.7, mb: 3 }}>
              {segmentData.intro}
            </Typography>

            {/* Recommended order summary */}
            {segmentData.steps && <RecommendedOrderSummary steps={segmentData.steps} />}

            {/* Card hierarchy */}
            {segmentData.primaryCard && (
              <>
                {/* Paso 1 — Primary card */}
                <ActionCard card={segmentData.primaryCard} isPrimary stepLabel="Paso 1" stepNumber={1} lng={lng} segment={segment} lp={lp} />

                {/* Paso 2, 3 — Secondary cards */}
                {segmentData.secondaryCards.map((card, i) => (
                  <ActionCard key={i} card={card} isPrimary={false} stepLabel={`Paso ${i + 2}`} stepNumber={i + 2} lng={lng} segment={segment} lp={lp} />
                ))}

                {/* Siguientes pasos — later/collapsed cards */}
                {segmentData.collapsedCards && segmentData.collapsedCards.length > 0 && (
                  <>
                    <SectionLabel label="Siguientes pasos" />

                    {segmentData.collapsedCards.length === 1 ? (
                      <ActionCard card={segmentData.collapsedCards[0]} isPrimary={false} isLater lng={lng} segment={segment} lp={lp} />
                    ) : (
                      <>
                        {/* Show first collapsed card always */}
                        <ActionCard card={segmentData.collapsedCards[0]} isPrimary={false} isLater lng={lng} segment={segment} lp={lp} />

                        <Collapse in={showMore}>
                          {segmentData.collapsedCards.slice(1).map((card, i) => (
                            <ActionCard key={i} card={card} isPrimary={false} isLater lng={lng} segment={segment} lp={lp} />
                          ))}
                        </Collapse>

                        {segmentData.collapsedCards.length > 1 && (
                          <Button
                            onClick={() => {
                              setShowMore((v) => !v);
                              if (!showMore) trackEvent('Result More Steps Expanded', { segment });
                            }}
                            aria-expanded={showMore}
                            endIcon={<ExpandMoreIcon sx={{ fontSize: 16, transition: 'transform 200ms', transform: showMore ? 'rotate(180deg)' : 'none' }} />}
                            sx={{
                              textTransform: 'none',
                              fontWeight: 500,
                              fontSize: '13px',
                              color: '#5F6B7A',
                              display: 'flex',
                              mx: 'auto',
                              mt: 0.5,
                              mb: 0,
                              minHeight: 36,
                              '&:hover': { color: '#1E3A5F', bgcolor: 'transparent' },
                            }}
                          >
                            {showMore ? 'Ver menos' : `Ver más (${segmentData.collapsedCards.length - 1})`}
                          </Button>
                        )}
                      </>
                    )}
                  </>
                )}
              </>
            )}

            {/* Common errors */}
            <ErroresComunes errors={segmentData.errors} />

            {/* Segment-specific email capture */}
            <EmailCapture copy={segmentData.emailCopy} segmentId={segment} lng={lng} />
          </Box>
        )}
      </Container>
      </Box>

      <TrustStrip />
    </Box>
  );
};

export default EmpiezaAquiPage;
